import React, { useState, useEffect } from 'react';
import { FiDollarSign, FiTrendingUp, FiCalendar, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import api from '@/services/api';
import { formatCurrency } from '@/utils/formatters';
import LoadingSpinner from '@/components/common/LoadingSpinner';

const StoreEarningsCard = () => {
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState(null);

  useEffect(() => {
    loadEarnings();
  }, []);

  const loadEarnings = async () => {
    try {
      const response = await api.get('/lojista/earnings');
      setEarnings(response.data);
    } catch (error) {
      console.error('Error loading earnings:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  if (!earnings) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 text-center text-gray-500">
        Não foi possível carregar os ganhos
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-800 p-4 text-white">
        <div className="flex items-center gap-3">
          <FiTrendingUp className="text-2xl" />
          <div>
            <h3 className="font-semibold">Seus Ganhos</h3>
            <p className="text-sm text-emerald-100">Acompanhe suas vendas e repasses</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-emerald-50 rounded-xl p-4">
            <p className="text-sm text-emerald-600">Total de Vendas</p>
            <p className="text-2xl font-bold text-emerald-700">{formatCurrency(earnings.total_sales)}</p>
            <p className="text-xs text-emerald-500">{earnings.total_orders} pedidos</p>
          </div>
          
          <div className="bg-blue-50 rounded-xl p-4">
            <p className="text-sm text-blue-600">Ganho Líquido</p>
            <p className="text-2xl font-bold text-blue-700">{formatCurrency(earnings.net_earnings)}</p>
            <p className="text-xs text-blue-500">Após taxas</p>
          </div>
        </div>

        {/* Period Earnings */}
        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
          <h4 className="font-medium text-gray-700 flex items-center gap-2">
            <FiCalendar className="text-gray-500" />
            Ganhos por Período
          </h4>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Hoje</span>
              <span className={`font-semibold ${earnings.earnings_today > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                {formatCurrency(earnings.earnings_today)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Esta semana</span>
              <span className={`font-semibold ${earnings.earnings_week > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                {formatCurrency(earnings.earnings_week)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Este mês</span>
              <span className={`font-semibold ${earnings.earnings_month > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                {formatCurrency(earnings.earnings_month)}
              </span>
            </div>
          </div>
        </div>

        {/* Fees Breakdown */}
        <div className="bg-orange-50 rounded-xl p-4">
          <div className="flex justify-between items-center">
            <span className="text-orange-700">Taxas da Plataforma</span>
            <span className="font-semibold text-orange-600">-{formatCurrency(earnings.platform_fees)}</span>
          </div>
        </div>

        {/* Payout Info (for PIX Platform users) */}
        {earnings.payment_method === 'pix_platform' && (
          <div className="bg-purple-50 rounded-xl p-4 space-y-2">
            <h4 className="font-medium text-purple-700">Repasse PIX</h4>
            
            {earnings.pending_payout > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-purple-600">Próximo repasse</span>
                <span className="font-bold text-purple-700">{formatCurrency(earnings.pending_payout)}</span>
              </div>
            )}
            
            {earnings.last_payout_date && (
              <div className="text-sm text-purple-500">
                Último repasse: {formatCurrency(earnings.last_payout_amount)} em {new Date(earnings.last_payout_date).toLocaleDateString('pt-BR')}
              </div>
            )}
            
            {!earnings.last_payout_date && earnings.pending_payout === 0 && (
              <p className="text-sm text-purple-500">Nenhum repasse ainda</p>
            )}
            
            <p className="text-xs text-purple-400 mt-2">
              Repasses são feitos toda segunda-feira
            </p>
          </div>
        )}

        {/* Recent Transactions */}
        {earnings.history && earnings.history.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-gray-700">Últimas Vendas</h4>
            <div className="max-h-48 overflow-y-auto space-y-2">
              {earnings.history.slice(0, 10).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      Pedido #{item.order_id.slice(-6)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(item.date).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-green-600">
                      <FiArrowUp className="inline mr-1" />
                      {formatCurrency(item.net)}
                    </p>
                    <p className="text-xs text-gray-400">
                      Total: {formatCurrency(item.total)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreEarningsCard;
